import React, { useState, useEffect, useRef, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import Navbar from "../components/Navbar";
import { useLocation } from "react-router-dom";
import languageContext from "../context/language/languageContext";

const PremiumEditablePlanUI = () => {
const [planData, setPlanData] = useState(null);
const [activeDay, setActiveDay] = useState(null);
const [editingDay, setEditingDay] = useState(null);
const [loading, setLoading] = useState(true);
const [showExtendModal, setShowExtendModal] = useState(false);
const [extendDays, setExtendDays] = useState("");
const [extending, setExtending] = useState(false);
const [saving, setSaving] = useState(false);
const saveTimeout = useRef(null);
const {
  setSessionTimeout,
  setAlert,
  setMessage,
} = useContext(languageContext);

 const location = useLocation();

const handleError = (error) => {
  if (error.response?.data?.detail === "Token has expired") {
    setSessionTimeout(true);  // 🔥 triggers logout modal
  } else {
    console.error("Error Details:", error.message);
    setAlert(true);
    setMessage("Please try again later, something went wrong");
  }
};

  const token = localStorage.getItem("token");
   const patient_id = location.state?.patient_id;


  const saveFullPlan = async (updatedData) => {
  try {
    setSaving(true);

    await axios.post(
      "https://www.careplus.gopillz.com/edit-plan",
      {
        plan_id: planData.plan_id,   // 👈 REQUIRED
        full_plan: updatedData,
      },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
     setEditingDay(null);
    setActiveDay(null);

  } catch (err) {
    console.error(err);
    handleError(err);
  } finally {
    setSaving(false);
    fetchPlan()
  }
};

const debouncedSave = (updatedData) => {
  if (saveTimeout.current) {
    clearTimeout(saveTimeout.current);
  }

  saveTimeout.current = setTimeout(() => {
    // saveFullPlan(updatedData);
  }, 1000);
};

const handleChange = (dayIndex, field, value) => {
  const updated = JSON.parse(JSON.stringify(planData)); // deep copy
  updated.plan[dayIndex][field] = value;

  setPlanData(updated);
  debouncedSave(updated);
};

const handleInstructionChange = (dayIndex, i, key, value) => {
  const updated = JSON.parse(JSON.stringify(planData));
  updated.plan[dayIndex].instructions[i][key] = value;

  setPlanData(updated);
  debouncedSave(updated);
};

const handleArrayChange = (dayIndex, field, i, value) => {
  const updated = JSON.parse(JSON.stringify(planData));
  updated.plan[dayIndex][field][i] = value;

  setPlanData(updated);
  debouncedSave(updated);
};

 const extendPlan = async () => {
  if (!extendDays || extendDays <= 0) {
    alert("Enter valid number of days");
    return;
  }

  try {
    setExtending(true);

    const response = await axios.post(
      "https://www.careplus.gopillz.com/extend-plan-ai",
      {
        plan_id: planData.plan_id,
        days: parseInt(extendDays), // 👈 API expects "days"
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    alert(response.data.message || "Plan extended successfully 🎉");

    setShowExtendModal(false);
    setExtendDays("");

    fetchPlan();

  } catch (err) {
    console.error(err);

    // =========================
    // 💳 CREDIT / LIMIT ERROR
    // =========================
    if (err.response?.status === 402) {
      alert(
        err.response?.data?.error ||
        "Your credits/free limit are exhausted."
      );

      return;
    }

    // =========================
    // ❌ OTHER API ERRORS
    // =========================
    if (err.response?.data?.error) {
      alert(err.response.data.error);
    } else {
      alert("Failed to extend ❌");
    }

    handleError(err);

  } finally {
    setExtending(false);
  }
};

  // 🔹 FETCH PLAN
 const fetchPlan = async () => {
  try {
    const res = await axios.post(
      "https://www.careplus.gopillz.com/full-plan",
      { id: patient_id },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setPlanData(res.data);

  } catch (err) {

    if (err.response?.status === 404) {
      // ✅ No plan exists
      setPlanData(null);   // triggers "No Plan Found" UI

    } else {
      handleError(err);    // token expired or other errors
    }

  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchPlan();
  }, []);

  // 🔹 UPDATE HANDLERS
  // const handleChange = (dayIndex, field, value) => {
  //   const updated = { ...planData };
  //   updated.plan[dayIndex][field] = value;
  //   setPlanData(updated);
  // };

  // const handleInstructionChange = (dayIndex, i, key, value) => {
  //   const updated = { ...planData };
  //   updated.plan[dayIndex].instructions[i][key] = value;
  //   setPlanData(updated);
  // };

  // const handleArrayChange = (dayIndex, field, i, value) => {
  //   const updated = { ...planData };
  //   updated.plan[dayIndex][field][i] = value;
  //   setPlanData(updated);
  // };


  // 🔹 LOADING
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        Loading plan...
      </div>
    );
  }

  // 🔹 EMPTY STATE
  if (!planData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
  <Navbar />

  <div className="flex flex-col items-center justify-center min-h-[85vh] px-4">

    {/* 🔥 PAGE TITLE */}
    <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
      🧠 Smart Recovery Plan
    </h1>

    <p className="text-gray-500 mb-8 text-center">
      Your personalized health journey starts here
    </p>

    {/* 💎 CARD */}
    <div className="bg-white/80 backdrop-blur-lg border border-gray-200 rounded-3xl shadow-xl p-10 max-w-md w-full text-center transition-all hover:shadow-2xl">

      {/* ICON */}
      <div className="flex justify-center mb-6">
        <div className="w-20 h-20 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white text-3xl shadow-lg animate-pulse">
          🧠
        </div>
      </div>

      {/* TITLE */}
      <h2 className="text-2xl font-semibold text-gray-800 mb-2">
        No Plan Found
      </h2>

      {/* DESCRIPTION */}
      <p className="text-gray-500 text-sm mb-6 leading-relaxed">
        You haven’t created your recovery plan yet.  
        Upload your prescription to generate a personalized daily plan powered by AI.
      </p>

      {/* PRIMARY BUTTON */}
      {/* <button
        onClick={() => handleView("/doctor_prescription", patient_id)}
        className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-xl font-medium shadow-md hover:scale-105 transition-all duration-300"
      >
        📤 Upload Prescription
      </button> */}

      {/* SECONDARY */}
      {/* <p className="text-xs text-gray-400 mt-4">
        Takes less than 10 seconds ⚡
      </p> */}

    </div>

  </div>
</div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Navbar />
      {saving && (
  <div className="fixed top-20 right-6 bg-blue-600 text-white px-4 py-2 rounded shadow">
    Saving...
  </div>
)}

      <div className="pt-24 px-4 md:px-10 max-w-6xl mx-auto">

        {/* HEADER */}
        <h1 className="text-3xl font-bold mb-6">🧠 Smart Recovery Plan</h1>
        <button
    onClick={() => setShowExtendModal(true)}
    className="bg-blue-600 text-white px-4 py-2 rounded-lg shadow hover:bg-blue-700"
  >
    ➕ Extend Plan
  </button>

        {/* PROGRESS */}
        <div className="bg-white p-6 rounded-2xl shadow mb-8">
          <div className="flex justify-between mb-2">
            <span>Day {planData.current_day} / {planData.total_days}</span>
            <span className="text-blue-600 font-semibold">
              {Math.round((planData.current_day / planData.total_days) * 100)}%
            </span>
          </div>

          <div className="w-full bg-gray-200 h-2 rounded-full">
            <div
              className="bg-blue-600 h-2 rounded-full"
              style={{
                width: `${(planData.current_day / planData.total_days) * 100}%`
              }}
            />
          </div>
        </div>

        {/* DAYS */}
        {planData.plan.map((day, index) => {
          const isOpen = activeDay === day.day;
          const isEditing = editingDay === index;
          const isToday = planData.current_day === day.day;

          return (
            <motion.div key={day.day} layout className="mb-6">

              <div className={`bg-white rounded-2xl shadow ${
                isToday ? "border-2 border-blue-500" : ""
              }`}>

                {/* HEADER */}
                <div
  onClick={() => {
    if (isOpen) {
      // ✅ closing → stop editing
      setActiveDay(null);
      setEditingDay(null);
    } else {
      // ✅ opening → just open
      setActiveDay(day.day);
    }
  }}
  className="p-5 flex justify-between cursor-pointer"
>
                  <div>
                    <h2 className="font-bold">Day {day.day}</h2>

                    {isEditing ? (
                      <input
                        value={day.focus}
                        onChange={(e)=>handleChange(index,"focus",e.target.value)}
                        className="border p-1 rounded"
                      />
                    ) : (
                      <p className="text-gray-500">{day.focus}</p>
                    )}
                  </div>

                  <button
                    onClick={(e)=>{
                      e.stopPropagation();
                      setEditingDay(index);
                      setActiveDay(day.day);
                    }}
                    className="text-blue-600"
                  >
                    Edit
                  </button>
                </div>

                {/* BODY */}
                <AnimatePresence>
                  {isOpen && (
                    <motion.div className="p-5 space-y-5">

                      {/* MEDICINES */}
                      <div>
                        <h3 className="text-green-600 font-semibold">💊 Medicines</h3>

                        {day.instructions.map((med,i)=>(
                          <div key={i} className="bg-green-50 p-3 rounded mb-2">

                            {isEditing ? (
                              <>
                                <input value={med.medicine}
                                  onChange={(e)=>handleInstructionChange(index,i,"medicine",e.target.value)}
                                  className="border w-full mb-1 p-1" />

                                <input value={med.dosage}
                                  onChange={(e)=>handleInstructionChange(index,i,"dosage",e.target.value)}
                                  className="border w-full mb-1 p-1" />

                                <input value={med.timing}
                                  onChange={(e)=>handleInstructionChange(index,i,"timing",e.target.value)}
                                  className="border w-full mb-1 p-1" />

                                <input value={med.precautions}
                                  onChange={(e)=>handleInstructionChange(index,i,"precautions",e.target.value)}
                                  className="border w-full p-1" />
                              </>
                            ) : (
                              <>
                                <p>{med.medicine}</p>
                                <p className="text-sm">{med.dosage} • {med.timing}</p>
                                <p className="text-xs">{med.precautions}</p>
                              </>
                            )}

                          </div>
                        ))}
                      </div>

                      {/* MONITOR */}
                      <div>
                        <h3 className="text-yellow-600 font-semibold">🧪 Monitor</h3>
                        {day.monitor.map((m,i)=>(
                          isEditing ? (
                            <input key={i}
                              value={m}
                              onChange={(e)=>handleArrayChange(index,"monitor",i,e.target.value)}
                              className="border w-full mb-1 p-1" />
                          ) : (
                            <p key={i}>• {m}</p>
                          )
                        ))}
                      </div>

                      {/* SOS */}
                      <div>
                        <h3 className="text-red-600 font-semibold">🚨 SOS</h3>
                        {day.sos_signs.map((s,i)=>(
                          isEditing ? (
                            <input key={i}
                              value={s}
                              onChange={(e)=>handleArrayChange(index,"sos_signs",i,e.target.value)}
                              className="border w-full mb-1 p-1" />
                          ) : (
                            <p key={i} className="text-red-600">• {s}</p>
                          )
                        ))}
                      </div>

                      {/* DOCTOR */}
                      <div>
                        <h3 className="text-blue-600 font-semibold">👨‍⚕️ Doctor Advice</h3>

                        {isEditing ? (
                          <input
                            value={day.doctor_alert}
                            onChange={(e)=>handleChange(index,"doctor_alert",e.target.value)}
                            className="border w-full p-1"
                          />
                        ) : (
                          <p>{day.doctor_alert}</p>
                        )}
                      </div>

                      {/* QUESTIONS */}
                      <div>
                        <h3 className="text-purple-600 font-semibold">📋 Questions</h3>
                        {day.check_in_questions.map((q,i)=>(
                          isEditing ? (
                            <input key={i}
                              value={q}
                              onChange={(e)=>handleArrayChange(index,"check_in_questions",i,e.target.value)}
                              className="border w-full mb-1 p-1" />
                          ) : (
                            <p key={i}>• {q}</p>
                          )
                        ))}
                      </div>

                      {/* SAVE */}
                      {isEditing && (
                       <button
  onClick={() => saveFullPlan(planData)}
  className="bg-blue-600 text-white px-4 py-2 rounded"
>
  Save All Changes
</button>
                      )}

                    </motion.div>
                  )}
                </AnimatePresence>

              </div>
            </motion.div>
          );
        })}
      </div>
      <AnimatePresence>
  {showExtendModal && (
    <motion.div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-2xl p-6 w-[90%] max-w-md shadow-xl"
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.8 }}
      >
        <h2 className="text-xl font-bold mb-4">Extend Plan</h2>

        <p className="text-gray-500 mb-3">
          Enter number of days to extend plan
        </p>

        <input
          type="number"
          value={extendDays}
          onChange={(e) => setExtendDays(e.target.value)}
          placeholder="e.g. 3"
          className="border w-full p-2 rounded mb-4"
        />

        <div className="flex justify-end gap-2">
          <button
            onClick={() => setShowExtendModal(false)}
            className="px-4 py-2 rounded bg-gray-200"
          >
            Cancel
          </button>

          <button
            onClick={extendPlan}
            disabled={extending}
            className="px-4 py-2 rounded bg-blue-600 text-white"
          >
            {extending ? "Extending..." : "Confirm"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )}
</AnimatePresence>
    </div>
  );
};

export default PremiumEditablePlanUI;